import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Positions a dropdown relative to its trigger (non-portal).
 *
 * @param {object} params
 * @param {import('react').RefObject<HTMLElement|null>} params.triggerRef
 * @param {import('react').RefObject<HTMLElement|null>} params.dropdownRef
 * @param {boolean} params.open
 * @param {'start'|'end'} [params.align='start']
 */
export function useAnchoredPosition({
  triggerRef,
  dropdownRef,
  open,
  align = 'start',
}) {
  const [style, setStyle] = useState({ top: 0, left: undefined, right: undefined });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const dropdown = dropdownRef.current;
    if (!trigger || !dropdown) return;

    const triggerRect = trigger.getBoundingClientRect();
    const parent = trigger.offsetParent;
    if (!parent || !(parent instanceof HTMLElement)) return;

    const parentRect = parent.getBoundingClientRect();
    const dropdownWidth = dropdown.offsetWidth || dropdown.getBoundingClientRect().width;
    const top = triggerRect.bottom - parentRect.top + 4;

    if (align === 'end') {
      const right = parentRect.right - triggerRect.right;
      setStyle({ top, left: undefined, right });
      return;
    }

    let left = triggerRect.left - parentRect.left;
    const maxLeft = parent.clientWidth - dropdownWidth - 8;
    if (maxLeft > 0) {
      left = Math.min(Math.max(left, 8), maxLeft);
    }
    setStyle({ top, left, right: undefined });
  }, [triggerRef, dropdownRef, align]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const ro = new ResizeObserver(() => updatePosition());
    if (triggerRef.current) ro.observe(triggerRef.current);
    if (dropdownRef.current) ro.observe(dropdownRef.current);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition, triggerRef, dropdownRef]);

  return style;
}
